import { IProfileV1 } from './user';

// Type definitions for Property entities

// Type alias for markdown strings
export type Markdown = string;

export interface Geolocation {
  type: "Point";
  coordinates: [number, number]; // [longitude, latitude]
}

export interface PropertyPhoto {
  url: string;
  width: number;
  height: number;
  aspect_ratio: number;
  date: number;
  order: number;
  lat?: number;
  long?: number;
}

export interface PropertyFAQ {
  question: string;
  answer: Markdown;
  order: number;
}

export interface PropertyDetailsContent {
  header: boolean;
  content: string | PropertyDetailsContent | PropertyDetailsContent[] | string[];
  copy?: boolean;
  link?: string;
  type?: 'text' | 'link';
}

export enum PropertyDetailsType {
  Directions = 'directions',
  Contact = 'contact',
  Info = 'info',
}

export enum PropertyDetailsSize {
  Small = 'small',
  Large = 'large',
}

export interface PropertyContent {
  description: Markdown;
  location: string;
  details: PropertyDetails[];
  faq: PropertyFAQ[];
}

export interface PropertyDetails {
  name: string;
  type: PropertyDetailsType;
  size: PropertyDetailsSize;
  contents: Markdown;
  order: number;
}

export type PropertyStatus = 
  | 'draft'
  | 'waiting_for_payment'
  | 'payment_successful'
  | 'payment_error'
  | 'published'
  | 'archived'
  | 'expired';

// Stripe Subscription interfaces (without importing Stripe library)
export type SubscriptionStatus = 
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'paused'
  | 'trialing'
  | 'unpaid';

export interface SubscriptionDiscount {
  id: string;
  object: 'discount';
  coupon: {
    id: string;
    object: 'coupon';
    duration: 'forever' | 'once' | 'repeating';
    duration_in_months?: number;
    name?: string;
    percent_off?: number;
    amount_off?: number;
    currency?: string;
    metadata?: Record<string, string>;
  };
  customer?: string;
  end?: number | null;
  start: number;
  subscription?: string;
}

export interface SubscriptionPlan {
  id: string;
  object: 'plan';
  active: boolean;
  amount: number;
  currency: string;
  interval: 'day' | 'week' | 'month' | 'year';
  interval_count: number;
  nickname?: string;
  product: string;
}

export interface StripeSubscription {
  id: string;
  object: 'subscription';
  status: SubscriptionStatus;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at?: number | null;
  ended_at?: number | null;
  trial_start?: number | null;
  trial_end?: number | null;
  plan: SubscriptionPlan;
  customer: string;
  created: number;
  billing_cycle_anchor: number;
  discount?: SubscriptionDiscount | null;
  discounts?: SubscriptionDiscount[];
  metadata?: Record<string, string>;
  items?: any;
}

export interface Property {
  name: string;
  contact: string;
  address: string;
  coordinates: {
    lon: number;
    lat: number;
  };
  geolocation?: Geolocation;
  photo?: PropertyPhoto;
  photos: PropertyPhoto[];
  default_language: string;
  active: boolean;
  deleted: boolean;
  created_date: number;
  updated_date: number;
  updated_status_date?: number;
  nano_id: string;
  uid: string;
  website: string;
  status?: PropertyStatus;
  url?: string;
  is_admin?: boolean;
  is_free_publish?: boolean;
  profile?: IProfileV1;
  subscription?: StripeSubscription;
  content: {
    [language: string]: PropertyContent;
  };
}

// API types
export type PropertyApiResponse = Property & {
  url?: string;
  invoice_pdf?: string;
};

export type PropertyApiRequest = Omit<Property, 'created_date' | 'updated_date' | 'nano_id' | 'uid'> & {
  created_date?: number;
  updated_date?: number;
  nano_id?: string;
  uid?: string;
  geolocation: Geolocation; // Required in request (not optional)
};
